import React, { useState } from "react";
import { User, Users, Mail, Phone, Calendar, CheckCircle, XCircle } from "lucide-react";
import Handler from "../../lib/Handlers/main";
import devLogInstance from "../../lib/loggerConfig";
import { UserData } from "../../../interfaces/main";

const IdBadges: React.FC<{ ids: string[]; type: "staff" | "group" }> = ({ ids, type }) => (
  <div className="flex flex-wrap gap-1">
    {ids.length > 0 ? (
      ids.map((id, index) => (
        <span
          key={index}
          className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full ${
            type === "staff"
              ? "bg-blue-50 text-blue-700 border border-blue-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          <span
            className={`w-1 h-1 rounded-full mr-1 ${
              type === "staff" ? "bg-blue-500" : "bg-green-500"
            }`}
          />
          {id}
        </span>
      ))
    ) : (
      <span className="text-xs text-gray-400 italic">None assigned</span>
    )}
  </div>
);

const UserCard: React.FC<{ user: UserData }> = ({ user }) => (
  <div className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
    <div className="flex items-start gap-4">
      {/* Left section - User info */}
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gray-50 rounded-full flex items-center justify-center border">
            <User className="h-5 w-5 text-gray-600" />
          </div>
          <div>
            <h3 className="font-medium">{user.full_name}</h3>
            <div className="text-xs text-gray-500 flex items-center gap-2">
              <span>ID: {user._id}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(user.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-3">
          <div className="text-sm flex items-center gap-2 text-gray-600">
            <Mail className="h-4 w-4 text-gray-400" />
            <a href={`mailto:${user.email}`} className="text-blue-600 hover:underline">
              {user.email}
            </a>
          </div>
          <div className="text-sm flex items-center gap-2 text-gray-600">
            <Phone className="h-4 w-4 text-gray-400" />
            {user.phonenumber}
          </div>
        </div>
      </div>

      {/* Right section - Status */}
      <div className="flex-shrink-0">
        {user.is_verified ? (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Verified
          </span>
        ) : (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
            <XCircle className="h-3 w-3 mr-1" />
            Unverified
          </span>
        )}
      </div>
    </div>

    {/* Bottom section - IDs */}
    <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-6">
      <div>
        <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-blue-500"></div>
          Staff Associations
        </div>
        <IdBadges ids={user.staff_ids} type="staff" />
      </div>
      <div>
        <div className="text-xs font-medium text-gray-500 mb-2 flex items-center gap-1">
          <div className="w-2 h-2 rounded-full bg-green-500"></div>
          Group Memberships
        </div>
        <IdBadges ids={user.group_ids} type="group" />
      </div>
    </div>
  </div>
);

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserData[]>([]);

  const fetchUsers = async () => {
    try {
      const res: UserData[] = await Handler.handleGettingUser();
      setUsers(res);
    } catch (error) {
      devLogInstance.error(error);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium flex items-center gap-2">
          <Users className="h-5 w-5 text-blue-600" />
          User Management
        </h2>
        <button
          onClick={fetchUsers}
          className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors duration-200 flex items-center gap-1"
        >
          <Users className="h-4 w-4" />
          Get Users
        </button>
      </div>

      <div className="space-y-4">
        {users.length > 0 ? (
          users.map((user) => <UserCard key={user._id} user={user} />)
        ) : (
          <div className="text-center py-8 bg-gray-50 rounded-lg">
            <Users className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Click "Get Users" to load user data</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
